const CHANNEL_NAME = 'vd-recorder-uploads-v1';
const RECORDING_ID = /^[A-Za-z0-9_-]{1,128}$/;

export interface RecorderUploadSignal {
  publish(recordingId: string): void;
  subscribe(listener: (recordingId: string) => void): () => void;
  dispose(): void;
}

/** Cross-tab invalidation only. IndexedDB remains authoritative. */
export class BrowserRecorderUploadSignal implements RecorderUploadSignal {
  private channel: BroadcastChannel | null = null;
  private readonly listeners = new Set<(recordingId: string) => void>();

  constructor(
    private readonly BroadcastChannelConstructor: typeof BroadcastChannel | undefined
      = globalThis.BroadcastChannel,
  ) {}

  publish(recordingId: string): void {
    if (!RECORDING_ID.test(recordingId)) return;
    try {
      this.ensureChannel()?.postMessage({ type: 'upload-changed', recordingId });
    } catch {
      this.closeChannel();
    }
  }

  subscribe(listener: (recordingId: string) => void): () => void {
    this.ensureChannel();
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  dispose(): void {
    this.closeChannel();
    this.listeners.clear();
  }

  private ensureChannel(): BroadcastChannel | null {
    if (this.channel || typeof this.BroadcastChannelConstructor !== 'function') return this.channel;
    this.channel = new this.BroadcastChannelConstructor(CHANNEL_NAME);
    this.channel.addEventListener('message', this.onMessage);
    return this.channel;
  }

  private closeChannel(): void {
    this.channel?.removeEventListener('message', this.onMessage);
    this.channel?.close();
    this.channel = null;
  }

  private onMessage = (event: MessageEvent<unknown>): void => {
    const detail = event.data;
    if (!detail || typeof detail !== 'object') return;
    const { type, recordingId } = detail as { type?: unknown; recordingId?: unknown };
    if (type !== 'upload-changed' || typeof recordingId !== 'string' || !RECORDING_ID.test(recordingId)) return;
    this.listeners.forEach((listener) => listener(recordingId));
  };
}
